import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CatalogActionEntry } from "@layer/dashboard/types";
import { api, type OauthClientRow } from "../lib/api";
import {
  deriveAgentScopeUniverse,
  groupScopesForClient,
  scopeSetsEqual,
  type ScopeFamilyGroup,
} from "../lib/oauth-scopes";
import { useCachedResource } from "../lib/resource-cache";

/**
 * MCP page — operator-facing read-only view of Layer's third
 * canonical-action surface.
 *
 * Phase 28a shipped this page reading from a static
 * `agent-descriptions.ts` SoT module. Phase 28b lifted those strings
 * into per-action `agentDescription` registry entries (validation
 * enforces presence for agent-callable actions) and DELETED the SoT
 * module. This page now reads from `/api/catalog` — every
 * agent-callable action surfaces with its `agent_description` field.
 *
 * The grouping (read-lookup / read-list / mutation-single /
 * mutation-saga / outbound / gdpr) is derived per-call from each
 * action's `compensation.kind` + side-effects + name prefix; no
 * separate category column on the registry.
 */
export function Mcp(): JSX.Element {
  // Phase 67 — cache-backed; shares the `catalog` key with the Catalog
  // page so the `/api/catalog` fetch is reused across both.
  const { data: catalog, error } = useCachedResource("catalog", () =>
    api.catalog(),
  );

  if (error !== null) {
    return (
      <div>
        <h1 className="page-title">MCP</h1>
        <p className="hint">Failed to load /api/catalog: {error.message}</p>
      </div>
    );
  }
  if (catalog === undefined) {
    return (
      <div>
        <h1 className="page-title">MCP</h1>
        <p className="hint">Loading…</p>
      </div>
    );
  }

  // Filter to agent-callable + with a description set. Group by
  // derived category so the page stays readable.
  const agentActions = catalog.actions.filter(
    (a) =>
      a.allowed_actors.includes("agent") &&
      typeof a.agent_description === "string" &&
      a.agent_description.length > 0,
  );
  const grouped = groupByDerivedCategory(agentActions);

  return (
    <div>
      <h1 className="page-title">MCP</h1>
      <p className="hint">
        Layer's third canonical-action surface — the Model Context Protocol
        server at <code>api.hercie.ai/mcp</code> (local dev: <code>localhost:3033</code>).
        REST is the first surface (the dashboard's <code>/api/dispatch/:action</code>);
        the CLI is the second; MCP is the third. AI agents (Claude, etc.)
        authenticate via OAuth 2.1 client_credentials grant + call canonical
        actions exposed as MCP tools, routed through the same dispatch
        chokepoint as every other surface.
      </p>

      <div className="panel">
        <h2>
          Tool descriptions · {agentActions.length} agent-callable canonical actions
        </h2>
        <p>
          These are the <code>agentDescription</code> registry strings the{" "}
          MCP server returns in its <code>tools/list</code> response under
          the <code>description</code> field. Phase 28b lifted these
          strings from the deleted{" "}
          <code>apps/dashboard/web/src/data/agent-descriptions.ts</code>{" "}
          SoT module into per-action registry entries. Human-readable
          mirror at{" "}
          <code>build-output/architecture/agent-descriptions.md</code>.
        </p>
        {(Object.keys(CATEGORY_LABELS) as DerivedCategory[]).map((cat) => {
          const entries = grouped.get(cat) ?? [];
          if (entries.length === 0) return null;
          const meta = CATEGORY_LABELS[cat];
          return (
            <section className="agent-description-group" key={cat}>
              <h3>
                {meta.label} · {entries.length}
              </h3>
              <p className="hint">{meta.description}</p>
              <table className="agent-description-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.name}>
                      <td>
                        <code>{e.name}</code>
                      </td>
                      <td>{e.agent_description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>

      <OauthClientsPanel actions={catalog.actions} />

      <div className="panel">
        <h2>Recent token mints</h2>
        <p>
          Every <code>POST /oauth/token</code> success or failure writes one{" "}
          <code>audit_log</code> row attributing to{" "}
          <code>system:oauth:&lt;client_id&gt;</code> (a new operational
          actor prefix on the existing <code>system:*</code> family — NOT a
          new actor kind). Filter the audit log directly at{" "}
          <Link to="/data/audit_log">/data/audit_log</Link>.
        </p>
        <p>
          Tool-call attributions appear as{" "}
          <code>agent:&lt;client_id&gt;</code> on the same{" "}
          <code>audit_log</code> table (phase 28b chokepoint round-trip).
        </p>
      </div>
    </div>
  );
}

/**
 * The category vocabulary is OWNED BY THE SERVER — `catalog/action-category.ts`
 * derives it from each action's `compensation` + `sideEffects` and ships it on
 * the catalog entry. Aliasing the wire type here keeps `CATEGORY_LABELS` below
 * exhaustive: add a category server-side and this page stops compiling until it
 * gains a matching label, instead of rendering an unlabelled group.
 *
 * This replaced a local `deriveCategory()` that matched action names literally.
 * Anything registered after that list was written fell through to
 * `mutation-single`, so new sagas and new outbound vendor calls were shown
 * under the wrong heading from the day they shipped.
 */
type DerivedCategory = CatalogActionEntry["category"];

const CATEGORY_LABELS: Record<
  DerivedCategory,
  { label: string; description: string }
> = {
  "read-lookup": {
    label: "Read · single entity lookups",
    description:
      "Read one row by primary key or unique identifier. Replays return the same shape with PII fields nulled.",
  },
  "read-list": {
    label: "Read · paginated lists",
    description:
      "Cursor-paginated reads with optional filters. Default limit 50, max 200.",
  },
  "mutation-single": {
    label: "Mutation · single-step writes",
    description:
      "Synchronous mutations through the chokepoint. Return the canonical id / vendor reference directly.",
  },
  "mutation-saga": {
    label: "Mutation · multi-step sagas (async)",
    description:
      "Return HTTP 202 + run_id synchronously; saga runs async via Inngest. Poll lookup_saga_run for terminal state.",
  },
  outbound: {
    label: "Outbound · vendor APIs",
    description:
      "Hit a vendor's API (Stripe, Resend). Idempotency keys flow through to the vendor as defense-in-depth.",
  },
  gdpr: {
    label: "GDPR",
    description:
      "Subject Access Request + erasure. Staff or operator only at MVP; agent widening requires the reserved scope grants.",
  },
};


function groupByDerivedCategory(
  entries: ReadonlyArray<CatalogActionEntry>,
): Map<DerivedCategory, CatalogActionEntry[]> {
  const m = new Map<DerivedCategory, CatalogActionEntry[]>();
  for (const e of entries) {
    const cat = e.category;
    if (!m.has(cat)) m.set(cat, []);
    m.get(cat)!.push(e);
  }
  return m;
}

// ---------------------------------------------------------------------------
// OAuth clients panel (2026-08-24) — the live per-client permission editor
// over `/api/oauth-clients/:slug`. Replaced the read-only "provision via the
// CLI" panel. The scope universe is DERIVED from the catalog on every render
// (`deriveAgentScopeUniverse` — the union of `required_scopes.agent` across
// agent-callable actions), so a new action's scope appears here the moment it
// registers, and a scope that unlocks nothing is never offered. The CLI verbs
// (`pnpm cli oauth client …`) remain as the second surface over the same
// repository.
// ---------------------------------------------------------------------------

/**
 * The one-time reveal — either a freshly-minted client_secret (new
 * client) or a ChatGPT connect code. Both are shown ONCE and only the
 * hash is stored, so the reveal is unmountable ONLY by its own dismiss.
 */
type Reveal =
  | {
      readonly kind: "secret";
      readonly client_id: string;
      readonly name: string;
      readonly value: string;
    }
  | {
      readonly kind: "connect_code";
      readonly client_id: string;
      readonly name: string;
      readonly value: string;
      readonly expires_at: string;
    };

function ScopeGrid(props: {
  readonly groups: ReadonlyArray<ScopeFamilyGroup>;
  readonly selected: ReadonlyArray<string>;
  readonly disabled: boolean;
  readonly idPrefix: string;
  readonly onToggle: (scope: string, checked: boolean) => void;
}): JSX.Element {
  const selectedSet = new Set(props.selected);
  return (
    <div className="oauth-scope-grid">
      {props.groups.map((g) => (
        <div className="oauth-scope-family" key={g.family}>
          <div className="oauth-scope-family-label">{g.family}</div>
          {g.scopes.map((s) => (
            <label
              className="oauth-scope-row"
              key={s.scope}
              title={
                s.unknown
                  ? "Legacy scope — no registered action requires it for agents. Untick to drop it on the next save."
                  : `Unlocks: ${s.unlocks.join(", ")}`
              }
            >
              <input
                type="checkbox"
                id={`${props.idPrefix}-${s.scope}`}
                checked={selectedSet.has(s.scope)}
                disabled={props.disabled}
                onChange={(e) => props.onToggle(s.scope, e.target.checked)}
              />
              <code>{s.scope}</code>
              <span className="hint">
                {s.unknown
                  ? "legacy"
                  : `${s.unlocks.length} tool${s.unlocks.length === 1 ? "" : "s"}`}
              </span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

function formatWhen(iso: string | null): string {
  if (iso === null) return "never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function OauthClientsPanel(props: {
  readonly actions: ReadonlyArray<CatalogActionEntry>;
}): JSX.Element {
  const { data: who } = useCachedResource("whoami", () => api.whoami());
  const slug = who?.studio.slug ?? null;

  const [clients, setClients] = useState<ReadonlyArray<OauthClientRow> | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  // Per-client draft scope lists, keyed by client_id. Absent = no edits.
  const [drafts, setDrafts] = useState<Record<string, ReadonlyArray<string>>>(
    {},
  );
  // client_id of the in-flight mutation, or "create". Disables that card.
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // The one-time reveal (client_secret OR connect code). Survives list
  // reloads by design — it CANNOT be re-fetched, so nothing may unmount it
  // except its own dismiss button.
  const [minted, setMinted] = useState<Reveal | null>(null);
  const [copied, setCopied] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newScopes, setNewScopes] = useState<ReadonlyArray<string>>([]);

  const universe = useMemo(
    () => deriveAgentScopeUniverse(props.actions),
    [props.actions],
  );

  useEffect(() => {
    if (slug === null) return;
    let cancelled = false;
    api
      .getOauthClients(slug)
      .then((r) => {
        if (!cancelled) {
          setClients(r.clients);
          setLoadError(null);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const reload = async (): Promise<void> => {
    if (slug === null) return;
    const r = await api.getOauthClients(slug);
    setClients(r.clients);
  };

  const toggleDraft = (
    client: OauthClientRow,
    scope: string,
    checked: boolean,
  ): void => {
    const current = drafts[client.client_id] ?? client.granted_scopes;
    const next = checked
      ? [...new Set([...current, scope])]
      : current.filter((s) => s !== scope);
    setDrafts({ ...drafts, [client.client_id]: next });
  };

  const saveScopes = async (client: OauthClientRow): Promise<void> => {
    if (slug === null) return;
    const draft = drafts[client.client_id];
    if (draft === undefined) return;
    setBusy(client.client_id);
    setActionError(null);
    try {
      await api.updateOauthClientScopes(slug, client.client_id, draft);
      const rest = { ...drafts };
      delete rest[client.client_id];
      setDrafts(rest);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (client: OauthClientRow): Promise<void> => {
    if (slug === null) return;
    const ok = window.confirm(
      `Revoke ${client.name} (${client.client_id})?\n\nEvery request it makes fails immediately — outstanding tokens die with it. This cannot be undone; create a new client to restore access.`,
    );
    if (!ok) return;
    setBusy(client.client_id);
    setActionError(null);
    try {
      await api.revokeOauthClient(slug, client.client_id);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const create = async (): Promise<void> => {
    if (slug === null) return;
    setBusy("create");
    setActionError(null);
    try {
      const r = await api.createOauthClient(slug, {
        name: newName.trim(),
        scopes: newScopes,
      });
      setMinted({
        kind: "secret",
        client_id: r.client.client_id,
        name: r.client.name,
        value: r.client_secret,
      });
      setCopied(false);
      setShowCreate(false);
      setNewName("");
      setNewScopes([]);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const issueConnectCode = async (client: OauthClientRow): Promise<void> => {
    if (slug === null) return;
    setBusy(client.client_id);
    setActionError(null);
    try {
      const r = await api.issueOauthConnectCode(slug, client.client_id);
      setMinted({
        kind: "connect_code",
        client_id: client.client_id,
        name: client.name,
        value: r.connect_code,
        expires_at: r.connect_code_expires_at,
      });
      setCopied(false);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const copySecret = async (): Promise<void> => {
    if (minted === null) return;
    try {
      await navigator.clipboard.writeText(minted.value);
      setCopied(true);
    } catch {
      // Clipboard can be unavailable (non-HTTPS dev hosts) — the value is
      // on screen either way; selection + ⌘C still works.
    }
  };

  const active = (clients ?? []).filter((c) => c.revoked_at === null);
  const revoked = (clients ?? []).filter((c) => c.revoked_at !== null);

  return (
    <div className="panel">
      <h2>OAuth clients · who may call this, and with what</h2>
      <p className="hint">
        Each client is a long-lived credential for one MCP-speaking agent.
        Its ticked scopes decide which tools <code>tools/list</code> offers
        it — default-deny, reads and writes ticked separately. A scope
        change takes effect on the client&apos;s next token
        (≤1&nbsp;hour, or relaunch the app);{" "}
        <strong>revocation is immediate</strong>. To connect Claude Desktop,
        hand over the <code>client_secret</code> from{" "}
        <strong>New client…</strong>; to connect{" "}
        <strong>ChatGPT</strong> (web → Settings → Connectors),
        press <strong>Connect ChatGPT…</strong> on a client for a one-time
        code the person pastes on the Layer approval page. Live table:{" "}
        <Link to="/data/oauth_clients">/data/oauth_clients</Link>; CLI twin:{" "}
        <code>pnpm cli oauth client …</code>.
      </p>
      {loadError !== null && (
        <p className="hint">Failed to load clients: {loadError}</p>
      )}
      {actionError !== null && (
        <p className="oauth-client-error">Request failed: {actionError}</p>
      )}
      {minted !== null && minted.kind === "secret" && (
        <div className="oauth-secret-reveal">
          <h3>
            Credentials for {minted.name} — the secret is shown ONCE
          </h3>
          <p className="hint">
            Only a hash is stored. Copy it now and put it straight into the
            client&apos;s config; if it is lost, revoke and create a new
            client.
          </p>
          <div className="oauth-secret-line">
            <span>client_id</span>
            <code>{minted.client_id}</code>
          </div>
          <div className="oauth-secret-line">
            <span>client_secret</span>
            <code>{minted.value}</code>
          </div>
          <div className="oauth-secret-actions">
            <button type="button" onClick={() => void copySecret()}>
              {copied ? "Copied ✓" : "Copy secret"}
            </button>
            <button
              type="button"
              onClick={() => {
                const ok = window.confirm(
                  "Dismiss the secret? It cannot be shown again.",
                );
                if (ok) setMinted(null);
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {minted !== null && minted.kind === "connect_code" && (
        <div className="oauth-secret-reveal">
          <h3>ChatGPT connect code for {minted.name} — shown ONCE</h3>
          <p className="hint">
            Send this to the person connecting ChatGPT (over something
            private — it grants this client&apos;s access). In ChatGPT
            web → Settings → Connectors → add a connector with the URL{" "}
            <code>https://api.hercie.ai/mcp</code>, then paste this code on
            the Layer approval page. It works once and expires{" "}
            {formatWhen(minted.expires_at)}.
          </p>
          <div className="oauth-secret-line">
            <span>connector URL</span>
            <code>https://api.hercie.ai/mcp</code>
          </div>
          <div className="oauth-secret-line">
            <span>connect code</span>
            <code>{minted.value}</code>
          </div>
          <div className="oauth-secret-actions">
            <button type="button" onClick={() => void copySecret()}>
              {copied ? "Copied ✓" : "Copy code"}
            </button>
            <button
              type="button"
              onClick={() => {
                const ok = window.confirm(
                  "Dismiss the connect code? It cannot be shown again — issue a fresh one if needed.",
                );
                if (ok) setMinted(null);
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {clients === null && loadError === null && (
        <p className="hint">Loading clients…</p>
      )}
      {clients !== null && active.length === 0 && (
        <p className="hint">No active clients.</p>
      )}
      {active.map((client) => {
        const draft = drafts[client.client_id] ?? client.granted_scopes;
        const dirty = !scopeSetsEqual(draft, client.granted_scopes);
        const groups = groupScopesForClient(universe, client.granted_scopes);
        const isBusy = busy === client.client_id;
        return (
          <div className="oauth-client-card" key={client.client_id}>
            <div className="oauth-client-head">
              <div>
                <strong>{client.name}</strong>{" "}
                <code>{client.client_id}</code>
              </div>
              <div className="hint">
                created {formatWhen(client.created_at)} · last used{" "}
                {formatWhen(client.last_used_at)} ·{" "}
                {client.granted_scopes.length} scope
                {client.granted_scopes.length === 1 ? "" : "s"}
              </div>
            </div>
            <ScopeGrid
              groups={groups}
              selected={draft}
              disabled={isBusy}
              idPrefix={client.client_id}
              onToggle={(scope, checked) =>
                toggleDraft(client, scope, checked)
              }
            />
            <div className="oauth-client-actions">
              <button
                type="button"
                disabled={!dirty || isBusy || draft.length === 0}
                onClick={() => void saveScopes(client)}
              >
                {isBusy ? "Saving…" : "Save scopes"}
              </button>
              {dirty && draft.length === 0 && (
                <span className="hint">
                  At least one scope is required — revoke instead to remove
                  access.
                </span>
              )}
              {dirty && draft.length > 0 && (
                <span className="hint">Unsaved changes.</span>
              )}
              <button
                type="button"
                disabled={isBusy}
                onClick={() => void issueConnectCode(client)}
                title="Mint a one-time code to connect ChatGPT (or any browser-OAuth MCP client) to this grant."
              >
                {isBusy ? "Working…" : "Connect ChatGPT…"}
              </button>
              <button
                type="button"
                className="oauth-revoke-btn"
                disabled={isBusy}
                onClick={() => void revoke(client)}
              >
                Revoke
              </button>
            </div>
          </div>
        );
      })}
      {showCreate ? (
        <div className="oauth-client-card oauth-client-card-new">
          <div className="oauth-client-head">
            <strong>New client</strong>
          </div>
          <label className="oauth-new-name">
            Name{" "}
            <input
              type="text"
              value={newName}
              maxLength={128}
              placeholder="e.g. Eladio — Claude Desktop"
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <ScopeGrid
            groups={groupScopesForClient(universe, [])}
            selected={newScopes}
            disabled={busy === "create"}
            idPrefix="new-client"
            onToggle={(scope, checked) =>
              setNewScopes(
                checked
                  ? [...new Set([...newScopes, scope])]
                  : newScopes.filter((s) => s !== scope),
              )
            }
          />
          <div className="oauth-client-actions">
            <button
              type="button"
              disabled={
                busy === "create" ||
                newName.trim().length === 0 ||
                newScopes.length === 0
              }
              onClick={() => void create()}
            >
              {busy === "create" ? "Creating…" : "Create client"}
            </button>
            <button
              type="button"
              disabled={busy === "create"}
              onClick={() => {
                setShowCreate(false);
                setNewName("");
                setNewScopes([]);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setShowCreate(true)}>
          New client…
        </button>
      )}
      {revoked.length > 0 && (
        <details className="oauth-revoked-list">
          <summary>
            Revoked · {revoked.length} client
            {revoked.length === 1 ? "" : "s"}
          </summary>
          {revoked.map((client) => (
            <div className="hint" key={client.client_id}>
              <code>{client.client_id}</code> {client.name} — revoked{" "}
              {formatWhen(client.revoked_at)}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
